import React, {FC, useCallback, useContext, useRef, useState} from 'react';
import {LocationType} from '@/common/constants/locationType';
import {LocationPoint} from '@/common/interfaces/location';
import {useLocalizer} from '@/common/services/localizer';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {ServicesContext} from '@/informers/contexts/ServicesContext';
import {LocationSearch, LocationSearchRef} from '@/common/components/LocationSearch';
import styles from './FormLocation.css';

const maxLocationsCount = 5;
const suggestCount = 50;
const allowedLocationTypes = [LocationType.Settlement];

interface FormLocationProps {
	onNextStage(): void;
}

export const FormLocation: FC<FormLocationProps> = props => {
	const {onNextStage} = props;

	const {informersAPI} = useContext(ServicesContext)!;
	const t = useLocalizer();

	const {
		state: {locations},
		dispatch,
	} = useInformerConfigState();

	const [newFieldShown, setNewFieldShown] = useState(locations.length < maxLocationsCount);
	const [editableIndex, setEditableIndex] = useState<number | null>(null);

	const editableFieldRef = useRef<LocationSearchRef>(null);

	const handleSearch = useCallback(
		(searchString: string) => {
			return informersAPI.getSearchSuggests(searchString, t.language, suggestCount, allowedLocationTypes);
		},
		[t.language],
	);

	const handleComplete = (event: React.FormEvent) => {
		event.preventDefault();
		onNextStage();
	};

	const handleFieldAdd = () => {
		setNewFieldShown(true);
	};

	const handleFieldRemove = (event: React.MouseEvent<HTMLElement>) => {
		const index = Number(event.currentTarget.dataset.index);
		dispatch({type: 'removeLocation', index});
	};

	const handleEditingCancel = () => {
		if (editableIndex !== null && locations[editableIndex]) {
			editableFieldRef.current!.setValue(locations[editableIndex].title);
		}
	};

	const handleLastValueClean = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.currentTarget.value === '') {
			dispatch({type: 'removeLocation', index: locations.length - 1});
			setNewFieldShown(true);
		}
	};

	const handleValueSelect = (location: LocationPoint) => {
		const alreadyExist = locations.some(item => item.id === location.id);
		if (alreadyExist) {
			// eslint-disable-next-line no-alert
			alert(t('locationDuplicate'));
			editableFieldRef.current!.reset();
			return;
		}

		if (editableIndex === null) {
			setNewFieldShown(false);
			dispatch({type: 'addLocation', location});
		} else {
			dispatch({type: 'replaceLocation', location, index: editableIndex});
		}
	};

	return (
		<form onSubmit={handleComplete}>
			{locations.map((selectedLocation, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<div key={selectedLocation.id} className={styles.row}>
					<LocationSearch
						ref={editableIndex === index ? editableFieldRef : undefined}
						className={styles.field}
						defaultValue={selectedLocation.title}
						fetchSuggest={handleSearch}
						onSelect={handleValueSelect}
						onFocus={() => setEditableIndex(index)}
						onBlur={editableIndex === index ? handleEditingCancel : undefined}
						onChange={index === locations.length - 1 ? handleLastValueClean : undefined}
					/>

					{!(index === 0 && locations.length === 0) &&
						!(index === locations.length - 1 && index !== maxLocationsCount - 1 && !newFieldShown) && (
							<button
								className={styles.removeButton}
								type='button'
								data-index={index}
								onClick={handleFieldRemove}
							/>
						)}

					{index === locations.length - 1 && !newFieldShown && locations.length !== maxLocationsCount && (
						<button className={styles.addButton} type='button' onClick={handleFieldAdd} />
					)}
				</div>
			))}

			{newFieldShown && (
				<div key={locations.length + 1} className={styles.row}>
					<LocationSearch
						ref={editableIndex === null ? editableFieldRef : undefined}
						className={styles.field}
						fetchSuggest={handleSearch}
						onSelect={handleValueSelect}
						onFocus={() => setEditableIndex(null)}
					/>
				</div>
			)}
		</form>
	);
};

FormLocation.displayName = 'FormLocation';
